import React, { useState, useEffect, useRef, useCallback } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './LifeStages.css';

// ДОБАВИЛ /vocabulary В ПУТЬ
const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const EXERCISES = [
  {
    id: 'chicken_growth',
    label: 'развитие курицы',
    items: [
      { id: 1, stage: 'яйцо', url: `${S3_BASE_URL}/egg.png`, order: 1 },
      { id: 2, stage: 'цыплёнок', url: `${S3_BASE_URL}/chicken.png`, order: 2 },
      { id: 3, stage: 'курица', url: `${S3_BASE_URL}/hen.png`, order: 3 },
    ],
  },
  {
    id: 'female_growth',
    label: 'жизнь женщины',
    items: [
      { id: 1, stage: 'девочка', url: `${S3_BASE_URL}/girl.png`, order: 1 },
      { id: 2, stage: 'девушка', url: `${S3_BASE_URL}/young_woman.png`, order: 2 },
      { id: 3, stage: 'женщина', url: `${S3_BASE_URL}/woman.png`, order: 3 },
      { id: 4, stage: 'бабушка', url: `${S3_BASE_URL}/grandmother.png`, order: 4 },
    ],
  },
  {
    id: 'male_growth',
    label: 'жизнь мужчины',
    items: [
      { id: 1, stage: 'мальчик', url: `${S3_BASE_URL}/boy.png`, order: 1 },
      { id: 2, stage: 'парень', url: `${S3_BASE_URL}/young_man.png`, order: 2 },
      { id: 3, stage: 'мужчина', url: `${S3_BASE_URL}/man.png`, order: 3 },
      { id: 4, stage: 'дедушка', url: `${S3_BASE_URL}/old_man.png`, order: 4 },
    ],
  },
];

const describeOrderMistake = (order, items) => {
  const correct = items.map(i => i.order).sort((a,b) => a-b);
  if (order.every((v, i) => v === correct[i])) return null;

  const names = order.map(o => items.find(i => i.order === o).stage);
  return `расставил в порядке «${names.join(' → ')}»`;
};

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const LifeStages = ({ onBack, onComplete }) => {
  const isCompleteRef = useRef(false);

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [exerciseLog, setExerciseLog] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const currentExercise = EXERCISES[currentExerciseIndex];
  const totalExercises = EXERCISES.length;

  // === ОЗВУЧКА ИНСТРУКЦИИ ===
  const { play: playInstructionAudio, stop: stopInstructionAudio } = useInstructionAudio();

  const playInstruction = useCallback(() => {
    playInstructionAudio('Расставь по порядку: что было сначала, а что потом');
  }, [playInstructionAudio]);

  // Запуск озвучки при смене задания
  useEffect(() => {
    playInstruction();
  }, [currentExerciseIndex, playInstruction]);

  // Сброс при загрузке нового задания
  useEffect(() => {
    if (currentExercise) {
      let shuffled = shuffleArray(currentExercise.items);
      while (shuffled.every((item, index) => item.order === index + 1)) {
        shuffled = shuffleArray(currentExercise.items);
      }
      setItems(shuffled);
      setIsComplete(false);
      isCompleteRef.current = false;
      setSelectedIndex(null);
      setDragIndex(null);
    }
  }, [currentExerciseIndex]);

  // Очистка озвучки при выходе
  useEffect(() => {
    return () => stopInstructionAudio();
  }, [stopInstructionAudio]);

  const swapItems = (index1, index2) => {
    const newItems = [...items];
    [newItems[index1], newItems[index2]] = [newItems[index2], newItems[index1]];
    setItems(newItems);
  };

  const handleCardClick = (clickedIndex) => {
    if (isComplete) return;
    if (selectedIndex === null) {
      setSelectedIndex(clickedIndex);
    } else if (selectedIndex === clickedIndex) {
      setSelectedIndex(null);
    } else {
      swapItems(selectedIndex, clickedIndex);
      setSelectedIndex(null);
    }
  };

  const handleDragStart = (e, index) => {
    if (isComplete) return;
    setSelectedIndex(null);
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    swapItems(dragIndex, targetIndex);
    setDragIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleOk = () => {
    if (isCompleteRef.current) return;
    
    const currentOrder = items.map(item => item.order);
    const isCorrect = currentOrder.every((val, idx) => val === idx + 1);
    const mistake = isCorrect ? null : describeOrderMistake(currentOrder, currentExercise.items);
    
    setAnswers([...answers, isCorrect]);
    setExerciseLog(prev => [...prev, {
      id: currentExercise.id,
      label: currentExercise.label,
      correct: isCorrect,
      order: currentOrder,
      mistake,
    }]);
    
    setIsComplete(true);
    isCompleteRef.current = true;
    setTimeout(() => {
      if (currentExerciseIndex < totalExercises - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
      } else {
        setShowResult(true);
      }
    }, 500);
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = answers.length > 0 ? Math.round((correctCount / answers.length) * 100) : 0;
        
        if (onComplete) {
          onComplete({
            testId: 'LifeStages',
            score,
            details: {
              total: totalExercises,
              correct: correctCount,
              exercises: exerciseLog,
            },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult, answers, exerciseLog, totalExercises, onComplete, onBack]);

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalExercises} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="ls-result-content">
          <h2 className="ls-result-title">🎉 Модуль завершён!</h2>
          <div className="ls-result-score">
            <span className="ls-score-number">{correctCount}</span>
            <span className="ls-score-total"> / {totalExercises}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalExercises} 
      forceStop={true}
      onComplete={onComplete || onBack}
      onRepeat={playInstruction} // Передаем функцию повтора озвучки
      extraContent="Расставь по порядку: что было сначала, а что потом"
      extraButtons={
        <button 
          className="ls-confirm-btn" 
          onClick={handleOk} 
          disabled={isComplete}
        >
          ОК
        </button>
      }
    >
      <div className="ls-items-row">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`ls-item-card ${selectedIndex === index ? 'selected-card' : ''} ${dragIndex === index ? 'dragging' : ''}`}
            onClick={() => handleCardClick(index)}
            draggable={!isComplete}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <img src={item.url} alt={item.stage} className="ls-item-img" />
          </div>
        ))}
      </div>
    </ModuleTemplate>
  );
};

export default LifeStages;